import { displayInitial } from '../utils/displayInitial'
import { dialog } from '../services/dialog'
import { copyToClipboard } from '../utils/clipboard'
import { mediaUrl } from '../services/config'
import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { adminApi, peopleApi } from '../services/api'
import type { AdminUserRow, AdminUsersPageResponse, AdminUserSort, AdminUserStatus } from '../services/api'
import {
  Users,
  Mail,
  Server,
  Copy,
  Trash2,
  Plus,
  X,
  Activity,
  Pencil,
  Shield,
  ShieldCheck,
  ShieldOff,
  Key,
  Eye,
  EyeOff,
  Check,
  Loader2,
  Layers,
  Search,
} from 'lucide-react'
import AppLayout from '../components/layout/AppLayout'
import ServerModulesModal from '../components/admin/ServerModulesModal'
import { Pagination, usePaginatedSlice } from '../components/ui/pagination'
import { AdminShell, type AdminTab } from '../components/admin/AdminShell'
import { getGroupedPlatformSections } from '../shell/admin-registry'
import { KpiCard, KpiRow, SectionCard, TablePanel, DataTable, type Column } from '../components/patterns'

interface UserInvite {
  id: string
  code: string
  email: string
  serverId: string
  createdAt: string
  usedAt: string | null
  usedById: string | null
  expiresAt: string | null
  server: { id: string; name: string }
  usedBy: { id: string; email: string; username: string } | null
}

interface AdminUser {
  id: string
  email: string
  username: string
  firstName?: string
  lastName?: string | null
  position?: string | null
  aboutMe?: string | null
  avatarUrl: string | null
  status: string
  role: string
  isAdmin: boolean
  accountStatus: 'DRAFT' | 'INVITED' | 'ACTIVE' | 'DISABLED'
  lastSeenAt?: string | null
  createdAt: string
  serverMembers: {
    server: { id: string; name: string }
    roles: { role: { id: string; name: string; color: string } }[]
  }[]
}

interface AdminServer {
  id: string
  name: string
  iconUrl: string | null
  owner: { id: string; email: string; username: string }
  _count: { members: number; channels: number }
  createdAt: string
}

function formatDateTime(value: string) {
  const d = new Date(value)
  return d.toLocaleString(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

// Shared shape the unified edit modal needs — satisfied by both the overview
// AdminUser rows and the paginated AdminUserRow rows.
type EditableUser = {
  id: string
  email: string
  username: string
  firstName?: string | null
  lastName?: string | null
  position?: string | null
  avatarUrl: string | null
  isAdmin: boolean
  accountStatus: 'DRAFT' | 'INVITED' | 'ACTIVE' | 'DISABLED'
  aboutMe?: string | null
}

const ACCOUNT_STATUS_TONE: Record<string, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  INVITED: 'bg-amber-500/15 text-amber-600',
  ACTIVE: 'bg-emerald-500/15 text-emerald-600',
  DISABLED: 'bg-rose-500/15 text-rose-600',
}

const USER_STATUS_OPTIONS: { value: AdminUserStatus; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'INVITED', label: 'Invited' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DISABLED', label: 'Disabled' },
]

function displayName(u: { firstName?: string | null; lastName?: string | null; username: string; email: string }) {
  const full = `${u.firstName || ''} ${u.lastName || ''}`.trim()
  return full || u.username || u.email
}

export default function AdminPanel() {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [invites, setInvites] = useState<UserInvite[]>([])
  const [users, setUsers] = useState<AdminUser[]>([])
  const [servers, setServers] = useState<AdminServer[]>([])
  const [loading, setLoading] = useState(false)

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteServerId, setInviteServerId] = useState('')
  const [inviteExpiry, setInviteExpiry] = useState('')

  const [serverName, setServerName] = useState('')
  const [showCreateServer, setShowCreateServer] = useState(false)
  const [editingUser, setEditingUser] = useState<EditableUser | null>(null)
  const [modulesFor, setModulesFor] = useState<{ id: string; name: string } | null>(null)
  const [editingServer, setEditingServer] = useState<AdminServer | null>(null)

  const [invitePage, setInvitePage] = useState(1)
  const [inviteSize, setInviteSize] = useState(10)
  const [serverPage, setServerPage] = useState(1)
  const [serverSize, setServerSize] = useState(10)

  // Users tab — server-side search / status / sort / pagination (mirrors the
  // Server-admin Members tab). Backed by GET /admin/users.page.
  const [uq, setUq] = useState('')
  const [uStatus, setUStatus] = useState<AdminUserStatus>('all')
  const [uAdminOnly, setUAdminOnly] = useState(false)
  const [uServerId, setUServerId] = useState('')
  const [uSort, setUSort] = useState<AdminUserSort>('name')
  const [uPage, setUPage] = useState(1)
  const [uPageSize, setUPageSize] = useState(50)
  const [uData, setUData] = useState<AdminUsersPageResponse | null>(null)
  const [uLoading, setULoading] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) return
    if (!user?.isAdmin) {
      navigate('/')
    }
  }, [isAuthenticated, user?.isAdmin, navigate])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [invitesRes, usersRes, serversRes] = await Promise.all([
        adminApi.getInvites(),
        adminApi.getUsers(),
        adminApi.getServers(),
      ])
      setInvites(invitesRes.data)
      setUsers(usersRes.data)
      setServers(serversRes.data)
      setInviteServerId((prev) => prev || (serversRes.data.length > 0 ? serversRes.data[0].id : ''))
    } catch (err: any) {
      console.error('Admin load error:', err)
      if (err.response?.status === 403) {
        navigate('/')
      }
    } finally {
      setLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    if (!isAuthenticated || !user?.isAdmin) return
    loadData()
  }, [loadData])

  const handleCreateInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteEmail || !inviteServerId) return
    try {
      const res = await adminApi.createInvite({
        email: inviteEmail,
        serverId: inviteServerId,
        // <input type="datetime-local"> emits a zone-less, second-less local
        // value ("2026-06-10T14:30"); the server wants a real instant, so
        // normalize to a UTC ISO-8601 string before POSTing. Sending the raw
        // value tripped z.string().datetime() → "Invalid datetime" (OSPROD-150).
        expiresAt: inviteExpiry ? new Date(inviteExpiry).toISOString() : null,
      })
      const createdFor = inviteEmail
      setInviteEmail('')
      setInviteExpiry('')
      loadData()
      // OSPROD-150: the invite link is emailed by the server now. Surface
      // an unambiguous result either way — silent table-append reads as
      // "the button did nothing".
      if (res.data?.emailSent) {
        void dialog.alert({ message: `Invite created — email sent to ${createdFor}` })
      } else {
        void dialog.alert({
          message: `Invite created, but the email to ${createdFor} could not be sent. Copy the link from the table and share it manually.`,
          tone: 'destructive',
        })
      }
    } catch (err: any) {
      void dialog.alert({ message: err.response?.data?.error || 'Failed to create invite', tone: 'destructive' })
    }
  }

  const handleDeleteInvite = async (id: string) => {
    if (!(await dialog.confirm({ message: 'Delete this invite?', tone: 'destructive', confirmLabel: 'Delete' }))) return
    try {
      await adminApi.deleteInvite(id)
      loadData()
    } catch {
      void dialog.alert({ message: 'Failed to delete invite', tone: 'destructive' })
    }
  }

  const handleCreateServer = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!serverName.trim()) return
    try {
      await adminApi.createServer({ name: serverName.trim() })
      setServerName('')
      setShowCreateServer(false)
      loadData()
    } catch (err: any) {
      void dialog.alert({ message: err.response?.data?.error || 'Failed to create server', tone: 'destructive' })
    }
  }

  const copyInviteLink = (code: string) => {
    const link = `${window.location.origin}/register?invite=${code}`
    void copyToClipboard(link)
  }

  const pendingInvites = useMemo(
    () => invites.filter((i) => !i.usedAt && (!i.expiresAt || new Date(i.expiresAt) > new Date())).length,
    [invites]
  )
  const adminUsers = useMemo(() => users.filter((u) => u.isAdmin).length, [users])
  const totalMembers = useMemo(() => servers.reduce((sum, s) => sum + (s._count?.members ?? 0), 0), [servers])

  const pagedInvites = usePaginatedSlice(invites, invitePage, inviteSize)
  const pagedServers = usePaginatedSlice(servers, serverPage, serverSize)

  const fetchUsersPage = useCallback(async () => {
    setULoading(true)
    try {
      const res = await adminApi.usersPage({
        q: uq.trim() || undefined,
        status: uStatus,
        admin: uAdminOnly ? 'admins' : undefined,
        serverId: uServerId || undefined,
        sort: uSort,
        page: uPage,
        pageSize: uPageSize,
      })
      setUData(res.data)
    } catch (err) {
      console.error('Failed to load users page:', err)
    } finally {
      setULoading(false)
    }
  }, [uq, uStatus, uAdminOnly, uServerId, uSort, uPage, uPageSize])

  // Refetch on any filter/sort/page change; debounce only the free-text search.
  useEffect(() => {
    if (!isAuthenticated || !user?.isAdmin) return
    const t = setTimeout(fetchUsersPage, uq ? 250 : 0)
    return () => clearTimeout(t)
  }, [fetchUsersPage, isAuthenticated, user?.isAdmin, uq])

  // Delegated global-admin sections come from the admin registry (the
  // in-core platform provider). Grouped: modules = one slot per catalog key
  // (People overrides the default adoption panel), platform = OS-level sections.
  // gateCtx for the global shell: a global admin, no server context.
  const grouped = useMemo(
    () => getGroupedPlatformSections({ isGlobalAdmin: !!user?.isAdmin, isServerOwner: false, enabledModules: null }),
    [user?.isAdmin],
  )
  const allSections = useMemo(() => [...grouped.modules, ...grouped.platform], [grouped])
  // Stable as long as allSections is stable (same admin boolean), so
  // the delegated panels don't remount on unrelated re-renders.
  const sectionLazies = useMemo(
    () => new Map(allSections.map((s) => [s.id, lazy(s.component)])),
    [allSections],
  )

  if (!user?.isAdmin) {
    return (
      <AppLayout>
        <div className="flex-1 flex items-center justify-center bg-background">
          <div className="text-destructive text-lg">Access denied</div>
        </div>
      </AppLayout>
    )
  }

  const localTabs: AdminTab[] = [
    { id: 'overview', label: 'Overview', icon: <Activity size={16} />, subtitle: 'High-level KPIs and recent activity' },
    { id: 'users',    label: 'Users',    icon: <Users size={16} />,    count: users.length,   subtitle: 'All registered users across servers' },
    { id: 'servers',  label: 'Servers',  icon: <Server size={16} />,   count: servers.length, subtitle: 'Workspaces and ownership' },
    { id: 'invites',  label: 'Invites',  icon: <Mail size={16} />,     count: pendingInvites, subtitle: 'Pending and used registration invites' },
  ]
  const moduleTabs: AdminTab[] = grouped.modules.map((s) => {
    const Ico = s.icon
    return { id: s.id, label: s.label, subtitle: s.subtitle, group: 'Modules', icon: Ico ? <Ico size={16} /> : undefined }
  })
  const platformTabs: AdminTab[] = grouped.platform.map((s) => {
    const Ico = s.icon
    return { id: s.id, label: s.label, subtitle: s.subtitle, group: 'Platform', icon: Ico ? <Ico size={16} /> : undefined }
  })
  const ADMIN_TABS: AdminTab[] = [...localTabs, ...moduleTabs, ...platformTabs]

  const inviteColumns: Column<UserInvite>[] = [
    { key: 'email', header: 'Email', render: (i) => <span className="text-foreground">{i.email}</span> },
    { key: 'server', header: 'Server', render: (i) => <span className="text-muted-foreground">{i.server?.name || i.serverId}</span> },
    {
      key: 'status', header: 'Status',
      render: (i) =>
        i.usedAt ? (
          <span className="text-emerald-500 text-xs bg-emerald-500/10 px-2 py-0.5 rounded-full">Used by {i.usedBy?.username || i.usedById}</span>
        ) : i.expiresAt && new Date(i.expiresAt) < new Date() ? (
          <span className="text-destructive text-xs bg-destructive/10 px-2 py-0.5 rounded-full">Expired</span>
        ) : (
          <span className="text-primary text-xs bg-primary/10 px-2 py-0.5 rounded-full">Pending</span>
        ),
    },
    { key: 'created', header: 'Created', render: (i) => <span className="text-muted-foreground tabular-nums">{new Date(i.createdAt).toLocaleDateString()}</span> },
    {
      key: 'actions', header: '', align: 'right',
      render: (i) => (
        <span className="whitespace-nowrap">
          {!i.usedAt && (
            <button onClick={() => copyInviteLink(i.code)} className="text-muted-foreground hover:text-foreground mr-2 transition" title="Copy invite link"><Copy size={14} /></button>
          )}
          <button onClick={() => handleDeleteInvite(i.id)} className="text-destructive hover:text-foreground transition" title="Delete invite"><Trash2 size={14} /></button>
        </span>
      ),
    },
  ]

  const deleteUserRow = async (u: AdminUserRow) => {
    if (!(await dialog.confirm({ message: `Delete user ${displayName(u)}? They will be hidden and signed out (soft delete, recoverable).`, tone: 'destructive', confirmLabel: 'Delete' }))) return
    try {
      await adminApi.deleteUser(u.id)
      fetchUsersPage()
    } catch (err: any) {
      void dialog.alert({ message: err.response?.data?.error || 'Failed to delete user', tone: 'destructive' })
    }
  }

  const userColumns: Column<AdminUserRow>[] = [
    {
      key: 'user', header: 'User',
      render: (u) => (
        <button onClick={() => setEditingUser(u)} className="flex items-center gap-2 text-left hover:opacity-80 transition" title="Edit user">
          <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-bold overflow-hidden shrink-0">
            {u.avatarUrl ? <img src={mediaUrl(u.avatarUrl)} alt="" className="w-full h-full object-cover" /> : displayInitial(u.username)}
          </div>
          <span className="text-foreground font-medium underline-offset-2 hover:underline truncate max-w-[160px]">{displayName(u)}</span>
          {u.isAdmin && (
            <span className="inline-flex items-center gap-1 text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wider whitespace-nowrap">
              <Shield size={10} /> Global admin
            </span>
          )}
        </button>
      ),
    },
    { key: 'position', header: 'Position', render: (u) => <span className="text-muted-foreground truncate max-w-[160px] block">{u.position || '—'}</span> },
    { key: 'email', header: 'Email', render: (u) => <span className="text-muted-foreground truncate max-w-[200px] block">{u.email}</span> },
    { key: 'servers', header: 'Servers', render: (u) => <span className="text-muted-foreground text-xs">{u.serverMembers.map((sm) => sm.server.name).join(', ') || '—'}</span> },
    {
      key: 'department', header: 'Department',
      render: (u) => {
        const members = uServerId ? u.serverMembers.filter((sm) => sm.server.id === uServerId) : u.serverMembers
        const names = members.map((sm) => sm.department?.name).filter(Boolean)
        return <span className="text-muted-foreground text-xs">{names.join(', ') || '—'}</span>
      },
    },
    {
      key: 'roles', header: 'Roles',
      render: (u) => {
        const roles = u.serverMembers.flatMap((sm) => sm.roles)
        if (roles.length === 0) return <span className="text-xs text-muted-foreground/60">—</span>
        return (
          <div className="flex gap-1 flex-wrap max-w-[180px]">
            {roles.map((r, i) => (
              <span key={`${r.role.id}-${i}`} className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: r.role.color + '20', color: r.role.color }}>{r.role.name}</span>
            ))}
          </div>
        )
      },
    },
    {
      key: 'status', header: 'Status',
      render: (u) => <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${ACCOUNT_STATUS_TONE[u.accountStatus] ?? ''}`}>{u.accountStatus}</span>,
    },
    { key: 'joined', header: 'Joined', render: (u) => <span className="text-muted-foreground tabular-nums whitespace-nowrap text-xs">{formatDateTime(u.createdAt)}</span> },
    {
      key: 'actions', header: '', align: 'right',
      render: (u) => (
        <span className="whitespace-nowrap">
          <button onClick={() => setEditingUser(u)} className="text-muted-foreground hover:text-foreground transition mr-1 p-1" title="Edit user"><Pencil size={14} /></button>
          <button onClick={() => deleteUserRow(u)} className="text-muted-foreground hover:text-destructive transition p-1" title="Delete user"><Trash2 size={14} /></button>
        </span>
      ),
    },
  ]

  const serverColumns: Column<AdminServer>[] = [
    { key: 'name', header: 'Name', render: (s) => <span className="text-foreground font-medium">{s.name}</span> },
    { key: 'owner', header: 'Owner', render: (s) => <span className="text-muted-foreground">{s.owner?.username || s.owner?.email}</span> },
    { key: 'members', header: 'Members', render: (s) => <span className="text-muted-foreground tabular-nums">{s._count.members}</span> },
    { key: 'channels', header: 'Channels', render: (s) => <span className="text-muted-foreground tabular-nums">{s._count.channels}</span> },
    { key: 'created', header: 'Created', render: (s) => <span className="text-muted-foreground tabular-nums">{new Date(s.createdAt).toLocaleDateString()}</span> },
    {
      key: 'actions', header: '', align: 'right',
      render: (s) => (
        <div className="flex items-center justify-end gap-2">
          <button onClick={() => setEditingServer(s)} className="text-muted-foreground hover:text-foreground transition" title="Edit server (name / owner)"><Pencil size={14} /></button>
          <button onClick={() => setModulesFor({ id: s.id, name: s.name })} className="text-muted-foreground hover:text-foreground transition" title="Configure modules"><Layers size={14} /></button>
        </div>
      ),
    },
  ]

  return (
    <>
      <AdminShell title="Global admin" icon={<Shield size={16} />} tabs={ADMIN_TABS}>
        {(active) => {
          if (loading) {
            return (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="py-12 text-center text-muted-foreground">Loading…</div>
              </div>
            )
          }

          if (active === 'overview') {
            return (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="p-6 space-y-6">
                  <KpiRow>
                    <KpiCard label="Users" value={users.length} accent="text-blue-500" icon={<Users size={18} />} />
                    <KpiCard label="Admins" value={adminUsers} accent="text-rose-500" icon={<Shield size={18} />} />
                    <KpiCard label="Servers" value={servers.length} accent="text-emerald-500" icon={<Server size={18} />} />
                    <KpiCard label="Total members" value={totalMembers} accent="text-violet-500" icon={<Users size={18} />} />
                    <KpiCard label="Pending invites" value={pendingInvites} accent="text-amber-500" icon={<Mail size={18} />} />
                  </KpiRow>
                  <SectionCard icon={<Activity size={14} />} title="Recently joined" subtitle="Last 5 users">
                    <div className="space-y-1">
                      {users.slice(0, 5).map((u) => (
                        <button key={u.id} onClick={() => setEditingUser(u)} className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-accent/40 transition text-left">
                          <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-bold">{displayInitial(u.username)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-foreground truncate">{u.username}</div>
                            <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                          </div>
                          <span className="text-xs text-muted-foreground tabular-nums">{formatDateTime(u.createdAt)}</span>
                        </button>
                      ))}
                      {users.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No users yet</div>}
                    </div>
                  </SectionCard>
                </div>
              </div>
            )
          }

          if (active === 'invites') {
            return (
              <div className="flex min-h-0 flex-1 flex-col gap-6 p-6">
                <SectionCard icon={<Mail size={14} />} title="Create invite" subtitle="One-time email invitation">
                  <form onSubmit={handleCreateInvite} className="space-y-3">
                    <div className="flex flex-wrap gap-3">
                      <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="Email address" className="flex-1 min-w-[220px] bg-background text-foreground text-sm rounded px-3 py-2 border border-border focus:border-primary outline-none" required />
                      <select value={inviteServerId} onChange={(e) => setInviteServerId(e.target.value)} className="bg-background text-foreground text-sm rounded px-3 py-2 border border-border focus:border-primary outline-none" required>
                        <option value="">Select server</option>
                        {servers.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                      </select>
                      <input type="datetime-local" value={inviteExpiry} onChange={(e) => setInviteExpiry(e.target.value)} className="bg-background text-foreground text-sm rounded px-3 py-2 border border-border focus:border-primary outline-none" />
                      <button type="submit" className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded text-sm font-semibold transition flex items-center gap-1.5"><Plus size={16} />Create</button>
                    </div>
                  </form>
                </SectionCard>
                <TablePanel
                  icon={<Mail size={16} />}
                  title="All invites"
                  count={invites.length}
                  className="min-h-0 flex-1"
                  footer={invites.length > 0 ? (
                    <Pagination total={invites.length} page={invitePage} pageSize={inviteSize} onPageChange={setInvitePage} onPageSizeChange={(s) => { setInviteSize(s); setInvitePage(1) }} />
                  ) : undefined}
                >
                  <DataTable columns={inviteColumns} rows={pagedInvites} rowKey={(i) => i.id} empty={<div className="py-12 text-center text-sm text-muted-foreground">No invites yet</div>} />
                </TablePanel>
              </div>
            )
          }

          if (active === 'users') {
            const sc = (uData?.statusCounts ?? {}) as Record<string, number>
            const allCount = (sc.DRAFT ?? 0) + (sc.INVITED ?? 0) + (sc.ACTIVE ?? 0) + (sc.DISABLED ?? 0)
            const total = uData?.total ?? 0
            const filtered = !!uq || uStatus !== 'all' || uAdminOnly
            return (
              <div className="flex min-h-0 flex-1 flex-col gap-3 p-6">
                {/* Search + sort + admins-only */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative flex-1 min-w-[240px] max-w-[400px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      value={uq}
                      onChange={(e) => { setUq(e.target.value); setUPage(1) }}
                      placeholder="Search by name, email, position…"
                      className="w-full bg-muted ring-1 ring-border rounded pl-9 pr-8 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-primary transition"
                    />
                    {uq && (
                      <button onClick={() => { setUq(''); setUPage(1) }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition" title="Clear search"><X size={12} /></button>
                    )}
                  </div>
                  <select value={uServerId} onChange={(e) => { setUServerId(e.target.value); setUPage(1) }} className="bg-muted ring-1 ring-border rounded px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-primary max-w-[200px]">
                    <option value="">All servers</option>
                    {servers.filter((s) => !s.name.startsWith('_')).map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <select value={uSort} onChange={(e) => { setUSort(e.target.value as AdminUserSort); setUPage(1) }} className="bg-muted ring-1 ring-border rounded px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-primary">
                    <option value="name">Sort: name</option>
                    <option value="joined">Sort: joined</option>
                    <option value="lastSeen">Sort: last seen</option>
                  </select>
                  <button
                    onClick={() => { setUAdminOnly((v) => !v); setUPage(1) }}
                    className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full ring-1 transition ${uAdminOnly ? 'bg-primary/15 text-primary ring-primary/30' : 'bg-muted text-muted-foreground ring-border hover:text-foreground'}`}
                  >
                    <Shield size={12} /> Admins only
                  </button>
                </div>
                {/* Status chips */}
                <div className="flex flex-wrap gap-1.5">
                  {USER_STATUS_OPTIONS.map((s) => {
                    const count = s.value === 'all' ? allCount : (sc[s.value] ?? 0)
                    const active = uStatus === s.value
                    return (
                      <button
                        key={s.value}
                        onClick={() => { setUStatus(s.value); setUPage(1) }}
                        className={`text-xs px-2.5 py-1 rounded-full ring-1 transition ${active ? 'bg-primary/15 text-primary ring-primary/30' : 'bg-muted text-muted-foreground ring-border hover:text-foreground'}`}
                      >
                        {s.label} <span className="tabular-nums opacity-70">· {count}</span>
                      </button>
                    )
                  })}
                </div>
                <TablePanel
                  icon={<Users size={16} />}
                  title="Users"
                  count={total}
                  subtitle="Everyone with an account"
                  className="min-h-0 flex-1"
                  footer={total > 0 ? (
                    <Pagination total={total} page={uPage} pageSize={uPageSize} onPageChange={setUPage} onPageSizeChange={(s) => { setUPageSize(s); setUPage(1) }} />
                  ) : undefined}
                >
                  <DataTable
                    columns={userColumns}
                    rows={uData?.users ?? []}
                    rowKey={(u) => u.id}
                    empty={<div className="py-12 text-center text-sm text-muted-foreground">{uLoading ? 'Loading…' : filtered ? 'No users match your filters' : 'No users yet'}</div>}
                  />
                </TablePanel>
              </div>
            )
          }

          if (active === 'servers') {
            return (
              <div className="flex min-h-0 flex-1 flex-col gap-6 p-6">
                {showCreateServer && (
                  <SectionCard
                    icon={<Server size={14} />}
                    title="Create server"
                    action={<button type="button" onClick={() => setShowCreateServer(false)} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>}
                  >
                    <form onSubmit={handleCreateServer} className="flex gap-3">
                      <input type="text" value={serverName} onChange={(e) => setServerName(e.target.value)} placeholder="Server name" className="flex-1 bg-background text-foreground text-sm rounded px-3 py-2 border border-border focus:border-primary outline-none" required />
                      <button type="submit" className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded text-sm font-semibold transition">Create</button>
                    </form>
                  </SectionCard>
                )}
                <TablePanel
                  icon={<Server size={16} />}
                  title="Servers"
                  count={servers.length}
                  subtitle="Workspaces and their owners"
                  className="min-h-0 flex-1"
                  actions={<button onClick={() => setShowCreateServer(true)} className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded text-xs font-semibold transition flex items-center gap-1.5"><Plus size={12} />New server</button>}
                  footer={servers.length > 0 ? (
                    <Pagination total={servers.length} page={serverPage} pageSize={serverSize} onPageChange={setServerPage} onPageSizeChange={(s) => { setServerSize(s); setServerPage(1) }} />
                  ) : undefined}
                >
                  <DataTable columns={serverColumns} rows={pagedServers} rowKey={(s) => s.id} empty={<div className="py-12 text-center text-sm text-muted-foreground">No servers yet</div>} />
                </TablePanel>
              </div>
            )
          }

          const Lazy = sectionLazies.get(active)
          if (Lazy) {
            return (
              <Suspense
                fallback={
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <div className="py-12 text-center text-muted-foreground">Loading…</div>
                  </div>
                }
              >
                <Lazy />
              </Suspense>
            )
          }

          return null
        }}
      </AdminShell>

      {editingUser && (
        <UserEditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); loadData(); fetchUsersPage() }}
          onRefresh={() => { loadData(); fetchUsersPage() }}
        />
      )}
      {modulesFor && (
        <ServerModulesModal
          serverId={modulesFor.id}
          serverName={modulesFor.name}
          onClose={() => setModulesFor(null)}
        />
      )}
      {editingServer && (
        <ServerEditModal
          server={editingServer}
          users={users}
          onClose={() => setEditingServer(null)}
          onSaved={(updated) => {
            setServers((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))
            setEditingServer(null)
          }}
        />
      )}
    </>
  )
}

/* ─── Server edit modal (OSPROD-209 — rename + reassign owner) ─── */
function ServerEditModal({
  server,
  users,
  onClose,
  onSaved,
}: {
  server: AdminServer
  users: AdminUser[]
  onClose: () => void
  onSaved: (s: AdminServer) => void
}) {
  const [name, setName] = useState(server.name)
  const [ownerId, setOwnerId] = useState(server.owner?.id || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const body: { name?: string; ownerId?: string } = {}
      if (name.trim() && name.trim() !== server.name) body.name = name.trim()
      if (ownerId && ownerId !== server.owner?.id) body.ownerId = ownerId
      if (!body.name && !body.ownerId) { onClose(); return }
      const res = await adminApi.updateServer(server.id, body)
      onSaved(res.data)
    } catch (err: any) {
      setError(err.response?.data?.error?.toString?.() || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-card rounded-xl shadow-2xl border border-border w-[480px] max-w-[95vw] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-foreground font-semibold">Edit server</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="text-sm bg-destructive/10 text-destructive rounded px-3 py-2">{error}</div>}
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Server name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full bg-muted text-foreground rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Owner</span>
            <select
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              className="mt-1 w-full bg-muted text-foreground rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">— Select owner —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── User edit modal (unified — mirrors the Server-admin Members modal) ─── */

function UserEditModal({
  user,
  onClose,
  onSaved,
  onRefresh,
}: {
  user: EditableUser
  onClose: () => void
  onSaved: () => void
  onRefresh: () => void
}) {
  const [form, setForm] = useState({
    username: user.username,
    email: user.email,
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    position: user.position || '',
    aboutMe: user.aboutMe || '',
    isAdmin: !!user.isAdmin,
  })
  const [accountStatus, setAccountStatus] = useState<EditableUser['accountStatus']>(user.accountStatus)
  const [saving, setSaving] = useState(false)
  const [actioning, setActioning] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pwOpen, setPwOpen] = useState(false)
  const [pwValue, setPwValue] = useState('')
  const [pwVisible, setPwVisible] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await adminApi.updateUser(user.id, {
        username: form.username,
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName || null,
        position: form.position || null,
        aboutMe: form.aboutMe || null,
        isAdmin: form.isAdmin,
      })
      onSaved()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const runAction = async (key: string, fn: () => Promise<unknown>, onOk?: () => void) => {
    setActioning(key)
    setError(null)
    try {
      await fn()
      onOk?.()
      onRefresh()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Action failed')
    } finally {
      setActioning(null)
    }
  }

  const handleDelete = async () => {
    if (!(await dialog.confirm({ message: `Delete user ${displayName(user)}? They will be hidden and signed out (soft delete, recoverable).`, tone: 'destructive', confirmLabel: 'Delete' }))) return
    await runAction('delete', () => adminApi.deleteUser(user.id), onSaved)
  }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl shadow-2xl ring-1 ring-border w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white text-sm font-bold overflow-hidden shrink-0">
            {user.avatarUrl ? <img src={mediaUrl(user.avatarUrl)} alt="" className="w-full h-full object-cover" /> : displayInitial(user.username)}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-foreground truncate">{displayName(user)}</h3>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
              <span className={`inline-block uppercase tracking-wider font-bold px-1.5 py-0.5 rounded text-[9px] ${ACCOUNT_STATUS_TONE[accountStatus] ?? ''}`}>{accountStatus}</span>
              {form.isAdmin && <span className="inline-flex items-center gap-1 text-primary font-semibold"><Shield size={10} /> Global admin</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto space-y-3">
          {error && <div className="text-sm bg-destructive/10 text-destructive rounded px-3 py-2">{error}</div>}

          <LabeledInput label="Username" value={form.username} onChange={(v) => setForm({ ...form, username: v })} />
          <LabeledInput label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <div className="grid grid-cols-2 gap-3">
            <LabeledInput label="First name" value={form.firstName} onChange={(v) => setForm({ ...form, firstName: v })} />
            <LabeledInput label="Last name" value={form.lastName} onChange={(v) => setForm({ ...form, lastName: v })} />
          </div>
          <LabeledInput label="Position" value={form.position} onChange={(v) => setForm({ ...form, position: v })} placeholder="e.g. Dispatcher" />
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">About</span>
            <textarea value={form.aboutMe} onChange={(e) => setForm({ ...form, aboutMe: e.target.value })} rows={3} className="w-full bg-muted rounded px-2.5 py-1.5 text-sm text-foreground ring-1 ring-border focus:outline-none focus:ring-primary transition resize-y" />
          </label>

          {/* Global administrator */}
          <div className="pt-3 mt-1 border-t border-border">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.isAdmin} onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })} className="mt-0.5 rounded border-border" />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-medium text-foreground"><Shield size={13} className="text-primary" /> Global administrator</span>
                <span className="block text-[11px] text-muted-foreground leading-snug mt-0.5">System-wide admin — full access to every server and all platform admin tools. Different from a per-server “Admin” role. Applied when you click Save.</span>
              </span>
            </label>
          </div>

          {/* Account actions */}
          <div className="pt-3 mt-1 border-t border-border">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Account actions</div>
            <div className="flex flex-wrap gap-2">
              {accountStatus !== 'ACTIVE' && (
                <ActionBtn icon={<Mail size={12} />} label={accountStatus === 'INVITED' ? 'Re-send invite' : 'Send invite'} busy={actioning === 'invite'} onClick={() => runAction('invite', () => peopleApi.sendInvite(user.id), () => setAccountStatus('INVITED'))} />
              )}
              {accountStatus !== 'DISABLED' && (
                <ActionBtn icon={<ShieldOff size={12} />} label="Disable" tone="destructive" busy={actioning === 'disable'} onClick={() => runAction('disable', () => peopleApi.bulkDisable([user.id]), () => setAccountStatus('DISABLED'))} />
              )}
              {accountStatus === 'DISABLED' && (
                <ActionBtn icon={<ShieldCheck size={12} />} label="Re-enable" busy={actioning === 'enable'} onClick={() => runAction('enable', () => peopleApi.bulkEnable([user.id]), () => setAccountStatus('ACTIVE'))} />
              )}
              {!pwOpen && <ActionBtn icon={<Key size={12} />} label="Set password" onClick={() => setPwOpen(true)} />}
              <ActionBtn icon={<Trash2 size={12} />} label="Delete" tone="destructive" busy={actioning === 'delete'} onClick={handleDelete} />
            </div>

            {pwOpen && (
              <div className="mt-2 bg-muted/40 ring-1 ring-border rounded-lg p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Set new password</div>
                <div className="relative">
                  <input type={pwVisible ? 'text' : 'password'} autoComplete="new-password" value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="Min 6 characters" className="w-full bg-card rounded px-2.5 py-1.5 pr-8 text-sm text-foreground ring-1 ring-border focus:outline-none focus:ring-primary transition" />
                  <button type="button" onClick={() => setPwVisible((v) => !v)} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition">{pwVisible ? <EyeOff size={12} /> : <Eye size={12} />}</button>
                </div>
                <p className="text-[10px] text-muted-foreground leading-tight">The user can sign in immediately. Draft/Invited accounts flip to Active. Share it out-of-band — we don't email it.</p>
                <div className="flex items-center gap-2 justify-end">
                  <button onClick={() => { setPwOpen(false); setPwValue(''); setPwVisible(false) }} className="text-xs px-3 py-1.5 rounded text-muted-foreground hover:bg-accent/40 hover:text-foreground transition">Cancel</button>
                  <button disabled={pwValue.length < 6 || actioning === 'password'} onClick={() => runAction('password', () => peopleApi.setPassword(user.id, pwValue), () => { setPwOpen(false); setPwValue(''); setPwVisible(false); setAccountStatus('ACTIVE') })} className="text-xs px-3 py-1.5 rounded bg-primary text-primary-foreground font-medium flex items-center gap-1.5 hover:bg-primary/90 transition disabled:opacity-50">{actioning === 'password' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Set</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition disabled:opacity-50 flex items-center gap-1.5">{saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save</button>
        </div>
      </div>
    </div>
  )
}

function LabeledInput({ label, value, onChange, type = 'text', placeholder }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">{label}</span>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="w-full bg-muted rounded px-2.5 py-1.5 text-sm text-foreground ring-1 ring-border focus:outline-none focus:ring-primary transition" />
    </label>
  )
}

function ActionBtn({ icon, label, onClick, busy, tone }: { icon: React.ReactNode; label: string; onClick: () => void; busy?: boolean; tone?: 'destructive' }) {
  return (
    <button onClick={onClick} disabled={busy} className={`text-xs px-2.5 py-1.5 rounded ring-1 flex items-center gap-1.5 transition disabled:opacity-50 ${tone === 'destructive' ? 'ring-destructive/30 text-destructive hover:bg-destructive/10' : 'ring-border text-foreground hover:bg-accent/40'}`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : icon}{label}
    </button>
  )
}

